import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors } from '@nucleus-commerce/admin-kit/errors';
import type { Supplier } from '@nucleus-commerce/api-client/platform';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { supplierFormFor, toSupplierRequest } from '../api/mappers';
import { useSaveSupplier } from '../hooks/use-save-supplier';
import {
  supplierSchema,
  type SupplierInput,
  type SupplierValues,
} from '../schemas/supplier-schema';

export interface SupplierDialogProps {
  open: boolean;
  /** Null adds a supplier. */
  supplier: Supplier | null;
  onClose: () => void;
}

export function SupplierDialog({ open, supplier, onClose }: SupplierDialogProps) {
  const { t } = useTranslation('diamonds');
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {supplier ? t('suppliers.editTitle', { name: supplier.name }) : t('suppliers.addTitle')}
          </DialogTitle>
          <DialogDescription>{t('suppliers.dialogDescription')}</DialogDescription>
        </DialogHeader>
        {open ? (
          <SupplierForm key={supplier?.id ?? 'new'} supplier={supplier} onDone={onClose} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function SupplierForm({ supplier, onDone }: { supplier: Supplier | null; onDone: () => void }) {
  const { t } = useTranslation(['diamonds', 'common']);
  const save = useSaveSupplier(supplier?.id ?? null);
  const form = useForm<SupplierInput, unknown, SupplierValues>({
    resolver: useI18nResolver(supplierSchema),
    defaultValues: supplierFormFor(supplier),
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError);

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await save.mutateAsync(toSupplierRequest(values));
      onDone();
    } catch (error) {
      applyFieldErrors(error);
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('diamonds:suppliers.fields.name')}</FormLabel>
              <FormControl>
                <Input autoFocus autoComplete="off" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="currency"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('diamonds:suppliers.fields.currency')}</FormLabel>
                <FormControl>
                  <Input autoComplete="off" maxLength={3} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="leadTimeDays"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('diamonds:suppliers.fields.leadTimeDays')}</FormLabel>
                <FormControl>
                  <Input inputMode="numeric" autoComplete="off" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('diamonds:suppliers.fields.notes')}</FormLabel>
              <FormControl>
                <Textarea rows={3} {...field} />
              </FormControl>
              <FormDescription>{t('diamonds:suppliers.fields.notesHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onDone}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? t('common:actions.saving') : t('common:actions.save')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
